import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { mergeWith, isEqual, kebabCase } from 'lodash';
// Context
import { StreamProvider } from './StreamProvider';
import { PreviewSyncContext, IPreviewSyncContext } from './PreviewSyncContext';
import { VersionHistoryContext } from './VersionHistoryContext';
// Hooks
import useToggle from 'hooks/useToggle';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Async
import ConsentsAsync from 'store/consents/consentsAsync';
// Types
import PageTypes from 'types/PageTypes';
import ConsentsStatuses from 'types/ConsentsStatuses';
import QuizTypes from 'types/QuizTypes';
// Models
import IQuestion from 'models/Question';
import IQuiz from 'models/Quiz';
import IPage from 'models/Page';
// Async
import { getQuiz } from 'store/quiz/quizzesAsync';
// Actions
import { QuizAgentActions } from 'store/quizAgent/quizAgentSlice';
import { setInitialField } from 'store/quiz/quizzesSlice';
// Selectors
import { selectDraftQuiz, selectLoading } from 'store/quizAgent/quizAgentSelectors';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
// Initial form
import { initialQuizForm } from 'store/form/formInitial';
// Layouts
import { Dialog, DialogTitle, DialogContent } from 'layouts/Dialog';
// Components
import { Button } from 'components/Buttons';
// Dialogs
import ConfirmationDialog from 'dialogs/Confirmation.dialog';
//
import QuizAgentCreateScreen from './QuizAgentCreateScreen';
import QuizAgentChatScreen from './QuizAgentChatScreen';
import QuizAgentFinalizeDialog from './QuizAgentFinalizeDialog';
import QuizStyleEditDialog from './QuizStyleEdit.dialog';
import VersionHistoryControls from './VersionHistoryControls';
// Icons
import { FaPalette } from 'react-icons/fa';
// i18next
import { useTranslation } from 'react-i18next';
// Utilities
import { encodeBase64 } from 'utilities/utilities';
import { quizToFormValues, formValuesToQuiz } from 'utilities/quizFormMapping';
// Hooks
import { useVersionHistory } from './useVersionHistory';

type Props = {
  open: boolean;
  onClose: () => void;
  quizId?: string;
  shouldFetchQuiz?: boolean;
};

// Arrays from the agent draftQuiz fully replace whatever the form had — we do
// NOT element-wise merge questions / pages / prizes / etc.
const replaceArraysMerge = (_objValue: any, srcValue: any) => {
  if (Array.isArray(srcValue)) return srcValue;
  return undefined;
};

const QuizAgentDialog: FC<Props> = ({ open, onClose, quizId, shouldFetchQuiz = false }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [step, setStep] = useState<number>(quizId ? 2 : 1);

  const quiz: IQuiz | null = useAppSelector(selectQuiz);
  const draftQuiz = useAppSelector(selectDraftQuiz);
  const loading = useAppSelector(selectLoading);

  // Single unified RHF form holding the entire quiz state in form-shape.
  const methods = useForm<any>({
    defaultValues: quizToFormValues(initialQuizForm),
  });

  // Linear undo/redo stack over the form above — see useVersionHistory for
  // what counts as "one version" (a settled agent reply, or a manually
  // committed edit).
  const history = useVersionHistory(methods);

  // Seed the preview hash synchronously so QuizAgentChatScreen — which may
  // render on the very first commit in edit mode (step starts at 2) — always
  // receives a valid base64 payload to decode. Subsequent updates flow through
  // recomputeHash().
  const [quizHash, setQuizHash] = useState<string>(() => {
    const dbShape = formValuesToQuiz(methods.getValues());
    dbShape._id = 'quiz-agent-draft-quiz';
    return encodeBase64(dbShape);
  });

  const lastAppliedDraftRef = useRef<any>(null);
  // Set whenever the draftQuiz-merge effect below actually applies a merge,
  // cleared once that turn's version is pushed. Guards the loading-based
  // push after it so a rejected/aborted stream (loading flips back to false
  // without any merge ever landing) doesn't create an empty version.
  const hasPendingAgentChangeRef = useRef(false);
  const debounceRef = useRef<any>(null);
  const quizRef = useRef<IQuiz | null>(quiz);
  // While a nested edit dialog (QuizPageEditForm) is open we freeze hash
  // recomputation so typing in inputs doesn't postMessage every iframe on
  // every keystroke. The dialog itself toggles this ref via PreviewSyncContext.
  const pausedRef = useRef(false);

  // Keep the latest quiz accessible to the watch subscription, which is set up
  // only once (so it must not rely on a captured `quiz` from first render).
  useEffect(() => {
    quizRef.current = quiz;
  }, [quiz]);

  // Recompute the preview hash (debounced by default) by converting current
  // form values back to DB-shape and base64-encoding. `force` overrides the
  // pause set by usePausePreviewSync — used by resume() to flush once.
  const recomputeHash = (immediate = false, force = false) => {
    if (pausedRef.current && !force) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const run = () => {
      const values = methods.getValues();
      const dbShape = formValuesToQuiz(values);
      if (!quizRef.current) dbShape._id = 'quiz-agent-draft-quiz';
      setQuizHash(encodeBase64(dbShape));
    };
    if (immediate) {
      run();
    } else {
      debounceRef.current = setTimeout(run, 300);
    }
  };

  // Stable handle for nested edit dialogs to suspend/resume the iframe-feeding
  // hash sync. Resume forces an immediate recompute so the preview catches
  // back up with whatever was edited while paused.
  const previewSyncApi = useMemo<IPreviewSyncContext>(() => ({
    pause: () => {
      pausedRef.current = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      // Nested edit dialogs (Page/Style/PromoVideo) already pause/resume
      // this on open/close — piggyback the same signal to also block
      // Undo/Redo, since undoing mid-edit would fight the dialog's own
      // snapshot-on-open/reset-on-cancel behaviour.
      history.lock();
    },
    resume: () => {
      pausedRef.current = false;
      recomputeHash(true, true);
      history.unlock();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // Initial fetch for edit-from-outside flow.
  useEffect(() => {
    if (!quizId || !shouldFetchQuiz) return;
    dispatch(getQuiz(quizId));
    // eslint-disable-next-line
  }, [quizId]);

  // Mount: load consents and seed the preview hash with the default form
  // values so the iframe has something to render before any agent activity.
  // Unmount: clear agent state and the quiz slice if we fetched it ourselves.
  useEffect(() => {
    // eslint-disable-next-line
    dispatch(ConsentsAsync.getConsents({ status: ConsentsStatuses.published, ['include.platform']: true }));
    recomputeHash(true);
    // No baseline is pushed here for the create flow: the empty template
    // isn't a state anyone wants to undo back to, and doing so would make
    // Undo silently wipe the whole quiz right after the agent first
    // generates it. Its first history entry is instead the agent-triggered
    // push once real content exists (see the `draftQuiz` effect below),
    // which naturally becomes an un-undoable version 1. Edit flow's
    // baseline IS meaningful (a real, already-saved quiz) and is pushed
    // once it loads instead (see the `quiz` effect below).
    return () => {
      dispatch(QuizAgentActions.setInitialState());
      if (shouldFetchQuiz) {
        dispatch(setInitialField('quiz'));
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
    // eslint-disable-next-line
  }, []);

  // When the existing quiz loads (edit mode), seed the unified form with the
  // DB→form mapping. Triggers an immediate hash recompute so the preview is
  // accurate before any agent activity.
  useEffect(() => {
    if (!quiz) return;
    methods.reset(quizToFormValues(quiz));
    recomputeHash(true);
    history.pushVersion('Original quiz', 'manual');
    // eslint-disable-next-line
  }, [quiz]);

  // Whenever the agent updates draftQuiz (initial createThread response or
  // stream chunks), map it to form-shape and merge into the current form
  // values. User edits to fields the agent did not touch are preserved.
  useEffect(() => {
    if (!draftQuiz || Object.keys(draftQuiz).length === 0) return;
    if (lastAppliedDraftRef.current === draftQuiz) return;
    lastAppliedDraftRef.current = draftQuiz;

    const draftFormValues = quizToFormValues(draftQuiz);
    const current = methods.getValues();

    const merged: any = mergeWith({}, current, draftFormValues, replaceArraysMerge);

    // The agent can't set progressBarColor directly (it's not exposed on the
    // update_quiz_theme tool) — keep it in sync with primaryColor here so the
    // progress bar always matches the theme color the agent picked.
    if (merged.theme?.primaryColor) {
      merged.theme.progressBarColor = merged.theme.primaryColor;
    }

    // Generate Page entries for any questions the agent added that don't yet
    // have a corresponding page — required so QuizFrame previews can render
    // them as their own screen.
    if (Array.isArray(draftQuiz.questions) && draftQuiz.questions.length) {
      const existingRefs = new Set(
        (merged.pages || []).flatMap((page: IPage) => page.refs || [])
      );
      const newQuestions = draftQuiz.questions.filter(
        (question: IQuestion) => !existingRefs.has(question.ref)
      );
      if (newQuestions.length) {
        merged.pages = [
          ...(merged.pages || []),
          ...newQuestions.map((question: IQuestion) => ({
            uuid: uuidv4(),
            refs: [question.ref],
            type: merged.type === QuizTypes.Survey ? PageTypes.AdditionalQuestions : PageTypes.Question,
          })),
        ];
      }
    }

    // Many turns are the agent just asking a clarifying question (topic,
    // difficulty, etc.) before it has generated anything — the backend still
    // echoes a draftQuiz on every one of those turns, so neither a reference
    // check on `draftQuiz` nor a value-diff against `current` reliably tells
    // us a turn actually produced something worth an undo step. What does:
    // whether the quiz has actually been named yet — QuizAgentChatScreen
    // uses the exact same signal (quizNameSlug) to show the "Generating"
    // placeholder instead of real screens. No name yet means nothing real
    // exists to snapshot, no matter how many Q&A round-trips happened.
    const mergedDbShape = formValuesToQuiz(merged);
    const isGenerated = !!(mergedDbShape.quizNameSlug || kebabCase(mergedDbShape.quizName));
    const hasRealChange = !isEqual(current, merged);

    methods.reset(merged, { keepDirty: false, keepTouched: true });
    recomputeHash(true);
    if (hasRealChange && isGenerated) {
      hasPendingAgentChangeRef.current = true;
    }
    // eslint-disable-next-line
  }, [draftQuiz]);

  // Subscribe to user-driven form changes for live preview updates.
  useEffect(() => {
    const subscription = methods.watch(() => {
      recomputeHash();
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line
  }, [methods]);

  // Commit exactly one version per full agent turn — on the loading
  // true→false edge, not per stream chunk — once the merge effect above has
  // actually applied the agent's draftQuiz to the form. Both createThread's
  // initial draft and the streamMessage reply that follows it collapse into
  // this single push, since createThread alone never flips loading to false.
  const wasLoadingRef = useRef(false);
  useEffect(() => {
    if (wasLoadingRef.current && !loading && hasPendingAgentChangeRef.current) {
      history.pushVersion('AI update', 'agent');
      hasPendingAgentChangeRef.current = false;
    }
    wasLoadingRef.current = loading;
    // eslint-disable-next-line
  }, [loading]);

  const { open: finalizeDialogOpen, toggle: toggleFinalizeDialog } = useToggle();
  const { open: styleEditOpen, toggle: toggleStyleEdit } = useToggle();

  // Closing mid-agent-session can drop an unsaved conversation/draft, so we
  // confirm before closing — except on the very first create step, where
  // there's nothing to lose yet.
  const [closeConfirmationOpen, setCloseConfirmationOpen] = useState<boolean>(false);
  const isFirstCreateStep = !quizId && step === 1;

  const handleRequestClose = () => {
    if (isFirstCreateStep) {
      onClose();
      return;
    }
    setCloseConfirmationOpen(true);
  };

  const versionHistoryApi = useMemo(() => ({ pushVersion: history.pushVersion }), [history.pushVersion]);

  return (
    <FormProvider {...methods}>
      <PreviewSyncContext.Provider value={previewSyncApi}>
      <VersionHistoryContext.Provider value={versionHistoryApi}>
      <StreamProvider>
        <Dialog size="full" open={open} onClose={handleRequestClose} bgColor="#242729">
          <DialogTitle
            title={quizId ? t('dialogs.quizAgent.editTitle') : t('dialogs.quizAgent.createTitle')}
            onClose={handleRequestClose}
            additionalButton={step > 1 ? (
              <div className="flex items-center gap-2">
                <VersionHistoryControls history={history} disabled={loading} />
                <Button variant="outlined" onClick={toggleStyleEdit} disabled={loading}>
                  <div className="flex items-center gap-2">
                    <FaPalette />
                    <span>{t('dialogs.quizAgent.styleEditorTooltip')}</span>
                  </div>
                </Button>
                <Button variant="contained" onClick={toggleFinalizeDialog} disabled={loading}>
                  {`${quizId ? t('dialogs.quizAgent.saveButton') : t('dialogs.quizAgent.createButton')}`}
                </Button>
              </div>
            ) : null}
          />
          <DialogContent disablePadding={step === 2}>
            <div className="flex gap-4 h-full">
              {step === 1 ? <QuizAgentCreateScreen setStep={setStep} /> : <QuizAgentChatScreen quizHash={quizHash} quizId={quizId} />}
            </div>
          </DialogContent>
        </Dialog>
        {finalizeDialogOpen ? (
          <QuizAgentFinalizeDialog open={finalizeDialogOpen} onClose={toggleFinalizeDialog} onCloseAgentDialog={onClose} />
        ) : null}
        {styleEditOpen ? (
          <QuizStyleEditDialog open={styleEditOpen} onClose={toggleStyleEdit} />
        ) : null}
        {closeConfirmationOpen ? (
          <ConfirmationDialog
            open={closeConfirmationOpen}
            loading={false}
            title={t('dialogs.quizAgent.closeConfirmTitle')}
            content={t('dialogs.quizAgent.closeConfirmContent')}
            onClose={() => setCloseConfirmationOpen(false)}
            onConfirm={onClose}
          />
        ) : null}
      </StreamProvider>
      </VersionHistoryContext.Provider>
      </PreviewSyncContext.Provider>
    </FormProvider>
  );
};

export default QuizAgentDialog;
